import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  Button,
  Box,
  Alert,
  CircularProgress,
  TextField,
  Typography,
} from "@mui/material";

import { resetPassword, verifyToken } from "../../../apis/admin";
import { AdminAuthService } from "../../../services/AdminAuthService";
import { AuthLayout } from "../../../components/AuthLayout";

const SUCCESS_MSG = "Token is valid. Welcome!";

export const AdminInvite = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState("Checking...");
  const [email, setEmail] = useState<string | null>(null);
  const [password, setPassword] = useState<string | null>(null);
  const [confirmPassword, setConfirmPassword] = useState<string | null>(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const token = searchParams.get("token");
  const dispatch = useDispatch();

  useEffect(() => {
    const verify = async () => {
      try {
        const res = await verifyToken(String(token) || "");
        if (res?.success) {
          setStatus(SUCCESS_MSG);
          setEmail(res.email);
        } else {
          setStatus(res.message);
        }
      } catch (error) {
        setStatus("Invalid or expired token.");
      }
    };

    if (token) {
      verify();
    } else {
      setStatus("No token found in the URL.");
    }
  }, [token]);

  const isPasswordValid = password !== null && password.length >= 8;

  const handleSubmit = async () => {
    try {
      const result = await AdminAuthService.login(email || "", password || "", dispatch);
      if (result) {
        window.location.href = "/dashboard";
      } else {
        window.location.href = "/login";
      }
    } catch (error: any) {
      console.error(error);
      window.location.href = "/login";
    }
  };

  const resetPass = async () => {
    setError("");
    if (!password) {
      setError("Password is required.");
      return;
    }
    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirmPassword) {
      setError("Passwords do not match. Please try again.");
      return;
    }
    setLoading(true);
    try {
      await resetPassword({ password, email, token });
      handleSubmit();
    } catch (err) {
      console.error(err);
      setError(`Failed to set password: ${err}`);
      setLoading(false);
    }
  };

  if (status !== SUCCESS_MSG) {
    return (
      <AuthLayout title="Invite" subtitle="Verifying your invitation">
        <Box sx={{ textAlign: "center", py: 4 }}>
          {status === "Checking..." ? (
            <CircularProgress />
          ) : (
            <Alert severity="error">{status}</Alert>
          )}
        </Box>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title="Set Your Password" subtitle="Create a password to get started">
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
        <TextField
          label="New Password"
          type="password"
          fullWidth
          onChange={(e) => setPassword(e.target.value)}
          helperText="Password must be at least 8 characters"
        />
        <TextField
          label="Confirm Password"
          type="password"
          fullWidth
          onChange={(e) => setConfirmPassword(e.target.value)}
        />
      </Box>
      <Button
        fullWidth
        variant="contained"
        color="secondary"
        disabled={loading || !isPasswordValid}
        onClick={resetPass}
        sx={{
          mt: 2.5,
          py: 1.3,
          fontSize: "0.938rem",
          fontWeight: 600,
        }}
      >
        {loading ? <CircularProgress size={24} color="inherit" /> : "Create & Login"}
      </Button>
    </AuthLayout>
  );
};
